// eslint-disable-next-line max-len
import { Component, Input, OnInit, AfterViewInit, OnDestroy, SimpleChanges, OnChanges, ElementRef, Output, EventEmitter, ViewChild } from '@angular/core';
import { KYMW68GetFeatureCodeListService } from './api-services/get-feature-code-list.service';
import { FeatureCodeComboBoxModel } from './feature-code-combo-box.model';
import { PlessHttp } from '../../../../shared/http/pless-http.service';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { PlessViewUtilsSub } from '../../../../commons/kymw00/pless-view-utils-sub.service';
import { PlessDialogServiceSub } from '../../../../commons/kymw00/pless-dialog-service-sub';
import { Feature } from './feature.model';

/**
 * 共通部品 特集コードコンボボックスを制御するクラス
 *
 * @export
 * @class FeatureCodeComboBoxComponent
 * @implements {OnInit}
 * @implements {AfterViewInit}
 * @implements {OnChanges}
 * @implements {OnDestroy}
 * @implements {ControlValueAccessor}
 */
@Component({
  selector: 'app-kymw68-feature-code-combo-box',
  templateUrl: 'feature-code-combo-box.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: Kymw68FeatureCodeComboBoxComponent,
      multi: true
    },
    {
      provide: KYMW68GetFeatureCodeListService,
      useFactory: (plessHttp: PlessHttp, dialogService: PlessDialogServiceSub) => {
        return new KYMW68GetFeatureCodeListService(plessHttp, dialogService);
      },
      deps: [PlessHttp, PlessDialogServiceSub]

    }
  ],
})

export class Kymw68FeatureCodeComboBoxComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {

  public selectedIndex: number = <number>-1;
  public selectedFeatureCode: string = null;
  public selectedFeatureName: string = <string>'';
  public selectedFeatureCodeName: string = <string>'';
  public model: FeatureCodeComboBoxModel = new FeatureCodeComboBoxModel();

  @ViewChild('comboBox', { static: true }) public comboBox: ElementRef;

  @Input() public subid: string = null;
  @Input() blank: boolean = <boolean>true;                                      // 空白フラグ
  @Input() initAutoLoad: boolean = <boolean>false;                              // オートロードフラグ
  @Input() mediumType: string = <string>'';                                     // 検索条件「媒体区分」を設定する(voの値)
  @Input() public tabindex: number = null;
  @Input() forcedEnabledMode: string = null;
  @Input() initFocusItem: any = null;                                           // ロード完了後フォーカスを移動させるid

  // レイアウト系の変数
  @Input() left: number = <number>0;
  @Input() top: number = <number>0;
  @Input() width: number | string;
  @Input() height: number = <number>20;
  @Input() required: boolean = <boolean>false;
  @Input() afterFocus: any = null;                                              // ロード完了後フォーカスを移動させるid
  @Input() visible: boolean = <boolean>true;                                    // 表示・非表示フラグ
  @Output() loadCompleted: EventEmitter<any> = new EventEmitter<any>();         // ロード完了通知

  /**
   * Creates an instance of Kymw68FeatureCodeComboBoxComponent.
   * @memberof Kymw68FeatureCodeComboBoxComponent
   * @param {KYMW68GetFeatureCodeListService} getFeatureCodeListService コードリストを取得する
   */
  constructor(
    public elementRef: ElementRef,
    private getFeatureCodeListService: KYMW68GetFeatureCodeListService,
  ) { }

  // ValueAccessor
  public disabled: boolean = <boolean>false;
  public onChange(_: any) { }
  public onTouched() { }
  public writeValue(text: string): void {
    if (text !== this.selectedFeatureCode) {
      this.selectedFeatureCode = text;
      this.setFeatureCode(text);
    }
  }
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * 初期化処理
   *
   * @memberof Kymw68FeatureCodeComboBoxComponent
   */
  ngOnInit(): void {
    // コンボボックスインスタンス設定
    this.model.component = this;
    this.comboBox.nativeElement.setAttribute('id', this.subid + '_sub');
    this.comboBox.nativeElement.setAttribute('forcedEnabledMode', this.forcedEnabledMode);
  }

  /**
   * ロード完了時
   *
   * @memberof Kymw68FeatureCodeComboBoxComponent
   */
  ngAfterViewInit(): void {
    // 初期データ自動取得フラグのチェック
    if (this.model.initAutoLoad) {
      // データ取得
      this.getData();
    }
  }

  /**
   * 値変更時イベント
   *
   * @param {SimpleChanges} changes
   * @memberof Kymw68FeatureCodeComboBoxComponent
   */
  ngOnChanges(changes: SimpleChanges): void {
    Object.keys(changes).forEach((propName) => {
      switch (propName) {
        case 'blank':
          // 先頭空白表示フラグ
          this.model.blank = changes.blank.currentValue;
          break;
        case 'initAutoLoad':
          // 初期データ自動取得フラグ
          this.model.initAutoLoad = changes.initAutoLoad.currentValue;
          break;
      }
    });
  }

  /**
   * コンポーネント終了時
   *
   * @memberof Kymw68FeatureCodeComboBoxComponent
   */
  ngOnDestroy(): void {
    // コンボボックス情報初期化
    this.clear();
  }

  /**
   * クリア
   *
   * @memberof Kymw68FeatureCodeComboBoxComponent
   */
  public clear(): void {
    // コンボボックス情報初期化
    this.selectedFeatureCode = <string>'';
    this.selectedFeatureName = <string>'';
    this.selectedFeatureCodeName = <string>'';
    this.selectedIndex = <number>-1;
    this.model = new FeatureCodeComboBoxModel();
    this.model.component = this;
    this.model.blank = this.blank;
  }

  /**
   * 媒体(コンボボックス)変更処理
   *
   * @memberof Kymw68FeatureCodeComboBoxComponent
   */
  setMediumType(mediumType: string, featureCode = '', endFunc: () => void = null) {
    this.reload(mediumType, featureCode, endFunc);
  }

  /**
   * コンボボックスの選択特集コードNameを切り替える
   *
   * @param {string} featureCode 特集コード
   * @memberof Kymw68FeatureCodeComboBoxComponent
   */
  public setFeatureCode(featureCode: string): void {
    this.model.setFeatureCode = featureCode;

    // ロード完了の場合
    if (this.model.completeUpdateFlg) {
      this.updateSelectedFeatureCode();
    }
  }

  /**
   * コンボボックスの選択コードNameを切り替える
   *
   * @memberof Kymw68FeatureCodeComboBoxComponent
   */
  public updateSelectedFeatureCode(): void {
    let tempList: Feature[] = this.model.dataList;
    if (PlessViewUtilsSub.isEmptyArray(tempList)) {
      tempList = [];
    }

    for (let i = Number(0); i < tempList.length; i++) {
      if (tempList[i].FeatureCode === this.model.setFeatureCode) {
        this.selectedIndex = i;
      }
    }

    if (this.initFocusItem != null) {
      if (this.initFocusItem instanceof HTMLElement) {
        // 参照先の型がElementならそのままフォーカスをあてる
        this.initFocusItem.focus();
      } else {
        // 参照先の型がElementでない(Component)ならComponent内の要素を見て最初のinput,textarea,selectElementにフォーカスをあてる
        for (const child of this.initFocusItem.elementRef.nativeElement.children) {
          if (child instanceof HTMLInputElement || child instanceof HTMLTextAreaElement || child instanceof HTMLSelectElement) {
            child.focus();
            break;
          }
        }
      }

      this.initFocusItem = null;
    }

    // バインドフィールドを更新
    this.updateBindedField(null);
  }

  /**
   * コンボボックスの内容を再度ロードする
   *
   * @param {string} [mediumType=''] 媒体区分
   * @memberof Kymw68FeatureCodeComboBoxComponent
   */
  public reload(mediumType = '', featureCode = '', endFunc: () => void = null): void {
    if (PlessViewUtilsSub.isEmpty(mediumType)) {
      this.clear();

      // コードリスト取得完了フラグを設定
      this.model.completeUpdateFlg = true;

      // 初期選択無しとする
      this.setFeatureCode('');
    } else {
      // 媒体区分があればリロード
      this.getData(mediumType, endFunc);
    }
  }

  /**
   * バインドフィールド初期化
   *
   * @private
   * @param {*} e
   * @returns {void}
   * @memberof Kymw68FeatureCodeComboBoxComponent
   */
  public updateBindedField(e: any): void {
    try {
      if (e != null) {
        this.selectedIndex = e.target.selectedIndex;
      }
      if (this.selectedIndex === -1 ||
        this.model.dataList == null ||
        // 空行なしなら１行未満で初期化
        (!this.blank && this.model.dataList.length < 1) ||
        // 空行ありなら２行未満で初期化
        (this.blank && this.model.dataList.length < 2)) {
        this.selectedFeatureCode = <string>'';
        this.selectedFeatureName = <string>'';
        this.selectedFeatureCodeName = <string>'';
        return;
      }

      this.selectedFeatureCode = this.model.dataList[this.selectedIndex].FeatureCode;
      this.selectedFeatureName = this.model.dataList[this.selectedIndex].FeatureName;
      this.selectedFeatureCodeName = this.model.dataList[this.selectedIndex].FeatureCodeName;
    } finally {
      if (this.initFocusItem != null) {
        let queryedElement;
        if (this.initFocusItem instanceof HTMLElement) {
          queryedElement = this.initFocusItem.querySelector('textarea, input, select');
        } else {
          queryedElement = this.initFocusItem.elementRef.nativeElement.querySelector('textarea, input, select');
        }
        (queryedElement ? <HTMLElement>queryedElement : this.initFocusItem).focus();
      }

      // ロード完了通知
      this.loadCompleted.emit(this.selectedFeatureCode);
    }
  }

  /**
   * データ取得
   *
   * @memberof Kymw68FeatureCodeComboBoxComponent
   */
  public getData(mediumType: string = null, endFunc: () => void = null): void {
    this.getFeatureCodeListService.execute(this.model, mediumType ? mediumType : this.mediumType, endFunc);
  }
}